<template>
  <div class="ratio-img" :style="containerStyle">
    <img
      :src="src"
      :alt="alt"
      :fetchpriority="fetchpriority"
      :class="computedImgClass"
      :style="computedImgStyle"
      v-bind="$attrs"
    >
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  /** URL изображения */
  src: string;
  /** Alt текст */
  alt?: string;
  /** Соотношение сторон (например: '16/9', '4/3', '1/1', или число 1.5) */
  ratio?: string | number;
  /** Режим подгонки изображения: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down' */
  fit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  /** Позиция изображения (например: 'center', 'top', 'bottom', '50% 50%') */
  position?: string;
  /** Приоритет загрузки: 'high' | 'low' | 'auto' */
  fetchpriority?: 'high' | 'low' | 'auto';
  /** CSS классы для изображения */
  imgClass?: string | string[] | Record<string, boolean>;
  /** CSS стили для изображения */
  imgStyle?: string | Record<string, string | number>;
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  ratio: '16/9',
  fit: 'cover',
  position: 'center',
  fetchpriority: 'auto'
});

// Вычисляем padding-bottom для соотношения сторон
const paddingBottom = computed(() => {
  if (typeof props.ratio === 'number') {
    return `${(1 / props.ratio) * 100}%`;
  }

  // Парсим строку вида '16/9'
  const parts = props.ratio.split('/');
  if (parts.length === 2) {
    const width = parseFloat(parts[0]);
    const height = parseFloat(parts[1]);
    if (!isNaN(width) && !isNaN(height) && width > 0) {
      return `${(height / width) * 100}%`;
    }
  }

  // Fallback - 16:9
  return '56.25%';
});

const containerStyle = computed(() => ({
  paddingBottom: paddingBottom.value
}));

const computedImgClass = computed(() => {
  const baseClass = `ratio-img__img ratio-img__img--${props.fit}`;

  if (!props.imgClass) {
    return baseClass;
  }

  // Если imgClass - строка
  if (typeof props.imgClass === 'string') {
    return `${baseClass} ${props.imgClass}`;
  }

  // Если imgClass - массив
  if (Array.isArray(props.imgClass)) {
    return [baseClass, ...props.imgClass];
  }

  // Если imgClass - объект
  return [baseClass, props.imgClass];
});

const computedImgStyle = computed(() => {
  const baseStyle: Record<string, string> = {
    objectPosition: props.position
  };

  if (!props.imgStyle) {
    return baseStyle;
  }

  // Если imgStyle - строка
  if (typeof props.imgStyle === 'string') {
    return props.imgStyle;
  }

  // Если imgStyle - объект
  return {
    ...baseStyle,
    ...props.imgStyle
  };
});
</script>

<style lang="scss" scoped>
.ratio-img {
  position: relative;
  width: 100%;
  height: 0;
  overflow: hidden;
}

.ratio-img__img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &--cover {
    object-fit: cover;
  }

  &--contain {
    object-fit: contain;
  }

  &--fill {
    object-fit: fill;
  }

  &--none {
    object-fit: none;
  }

  &--scale-down {
    object-fit: scale-down;
  }
}
</style>
